import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Breadcrumbs,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  CircularProgress,
  Chip,
  Tooltip,
  Grid,
  IconButton,
} from '@mui/material';
import {
  Storage as StorageIcon,
  NavigateNext as NavigateNextIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  Sync as SyncIcon,
  SettingsBackupRestore as RestoreIcon,
} from '@mui/icons-material';
import Select from '../../../components/Select';
import DateRangePicker from '../../../components/DateRangePicker';
import TimePicker from '../../../components/TimePicker';
import Table from '../../../components/Table';
import { formatDateTimeWithCulture } from '../../../utils/dateUtils';
import {
  fetchBackupConfigRequest,
  selectBackupConfig,
  saveBackupConfigRequest,
  selectSaveLoading,
  selectSaveSuccess,
  selectSaveSuccessMessage,
  selectSaveError,
  clearSaveStatus,
  manualBackupRequest,
  selectManualBackupLoading,
  selectManualBackupSuccess,
  selectManualBackupSuccessMessage,
  selectManualBackupError,
  clearManualBackupStatus,
  fetchBackupLogsRequest,
  selectBackupLogs,
  selectBackupLogsLoading,
  selectBackupLogsTotal,
  selectBackupLogsPage,
  selectBackupLogsSize,
  setBackupLogsPage,
  setBackupLogsSize,
  selectFilterFromDate,
  selectFilterToDate,
  setFilterFromDate,
  setFilterToDate,
  restoreBackupRequest,
  selectRestoreLoading,
  selectRestoreSuccess,
  selectRestoreSuccessMessage,
  selectRestoreError,
  clearRestoreStatus,
} from './slice';
import { useNotification } from '../../../components/Notification';

// Schedule type constants (matching API: 0=Event, 1=Daily, 2=Weekly)
const SCHEDULE_TYPE = {
  EVENT: 0,
  DAILY: 1,
  WEEKLY: 2,
};

// Days of week (0=Sunday, 1=Monday, ..., 6=Saturday)
const DAYS_OF_WEEK = [
  { key: 0, label: 'systemBackup.days.sunday' },
  { key: 1, label: 'systemBackup.days.monday' },
  { key: 2, label: 'systemBackup.days.tuesday' },
  { key: 3, label: 'systemBackup.days.wednesday' },
  { key: 4, label: 'systemBackup.days.thursday' },
  { key: 5, label: 'systemBackup.days.friday' },
  { key: 6, label: 'systemBackup.days.saturday' },
];

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`systemBackup-tabpanel-${index}`}
      aria-labelledby={`systemBackup-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function SystemBackup() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();
  const [tabValue, setTabValue] = useState(0);

  // Backup config state from Redux
  const backupConfig = useSelector(selectBackupConfig);
  const saveLoading = useSelector(selectSaveLoading);
  const saveSuccess = useSelector(selectSaveSuccess);
  const saveSuccessMessage = useSelector(selectSaveSuccessMessage);
  const saveError = useSelector(selectSaveError);
  const manualBackupLoading = useSelector(selectManualBackupLoading);
  const manualBackupSuccess = useSelector(selectManualBackupSuccess);
  const manualBackupSuccessMessage = useSelector(selectManualBackupSuccessMessage);
  const manualBackupError = useSelector(selectManualBackupError);
  // Backup logs state from Redux
  const backupLogs = useSelector(selectBackupLogs);
  const backupLogsLoading = useSelector(selectBackupLogsLoading);
  const backupLogsTotal = useSelector(selectBackupLogsTotal);
  const backupLogsPage = useSelector(selectBackupLogsPage);
  const backupLogsSize = useSelector(selectBackupLogsSize);
  // Filter state
  const filterFromDate = useSelector(selectFilterFromDate);
  const filterToDate = useSelector(selectFilterToDate);
  // Restore state
  const restoreLoading = useSelector(selectRestoreLoading);
  const restoreSuccess = useSelector(selectRestoreSuccess);
  const restoreSuccessMessage = useSelector(selectRestoreSuccessMessage);
  const restoreError = useSelector(selectRestoreError);

  // Backup config state
  const [scheduleType, setScheduleType] = useState(null);
  const [executionTime, setExecutionTime] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);

  // Fetch backup config on mount
  useEffect(() => {
    dispatch(fetchBackupConfigRequest());
  }, [dispatch]);

  // Populate form when backupConfig changes
  useEffect(() => {
    if (backupConfig) {
      setScheduleType(backupConfig.scheduleType ?? null);
      setExecutionTime(backupConfig.scheduleTime || '');
      setSelectedDay(backupConfig.scheduleDay ?? null);
    } else {
      // Clear form when no config found
      setScheduleType(null);
      setExecutionTime('');
      setSelectedDay(null);
    }
  }, [backupConfig]);

  // Show notification on save success/error
  useEffect(() => {
    if (saveSuccess) {
      showNotification(saveSuccessMessage || t('systemBackup.config.saveSuccess'), 'success');
      dispatch(clearSaveStatus());
    }
    if (saveError) {
      showNotification(saveError, 'error');
      dispatch(clearSaveStatus());
    }
  }, [saveSuccess, saveSuccessMessage, saveError, showNotification, dispatch, t]);

  // Show notification on manual backup success/error
  useEffect(() => {
    if (manualBackupSuccess) {
      showNotification(manualBackupSuccessMessage || t('systemBackup.config.manualBackupSuccess'), 'success');
      dispatch(clearManualBackupStatus());
    }
    if (manualBackupError) {
      showNotification(manualBackupError, 'error');
      dispatch(clearManualBackupStatus());
    }
  }, [manualBackupSuccess, manualBackupSuccessMessage, manualBackupError, showNotification, dispatch, t]);

  // Show notification on restore success/error
  useEffect(() => {
    if (restoreSuccess) {
      showNotification(restoreSuccessMessage || t('systemBackup.history.restoreSuccess'), 'success');
      dispatch(clearRestoreStatus());
    }
    if (restoreError) {
      showNotification(restoreError, 'error');
      dispatch(clearRestoreStatus());
    }
  }, [restoreSuccess, restoreSuccessMessage, restoreError, showNotification, dispatch, t]);

  // Fetch backup logs when Tab 2 is selected or pagination/filters change
  useEffect(() => {
    if (tabValue === 1) {
      dispatch(fetchBackupLogsRequest({ page: backupLogsPage, size: backupLogsSize }));
    }
  }, [dispatch, tabValue, backupLogsPage, backupLogsSize, filterFromDate, filterToDate]);

  // Schedule type options for Select component
  const scheduleTypeOptions = [
    { id: SCHEDULE_TYPE.WEEKLY, name: t('systemBackup.config.weekly') },
    { id: SCHEDULE_TYPE.DAILY, name: t('systemBackup.config.daily') },
  ];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleScheduleTypeChange = (event) => {
    setScheduleType(event.target.value);
  };

  const handleExecutionTimeChange = (event) => {
    setExecutionTime(event.target.value);
  };

  const handleDayChange = (event, newDay) => {
    if (newDay !== null) {
      setSelectedDay(newDay);
    }
  };

  const handleSave = () => {
    const configData = {
      scheduleType: scheduleType,
      scheduleTime: executionTime || null,
      scheduleDay: selectedDay,
    };
    dispatch(saveBackupConfigRequest(configData));
  };

  const handleManualBackup = () => {
    dispatch(manualBackupRequest());
  };

  // Backup logs pagination handlers
  const handleLogsPageChange = (newPage) => {
    dispatch(setBackupLogsPage(newPage));
  };

  const handleLogsPageSizeChange = (newSize) => {
    dispatch(setBackupLogsSize(newSize));
  };

  // Filter handlers
  const handleFilterFromDateChange = (event) => {
    dispatch(setFilterFromDate(event.target.value));
  };

  const handleFilterToDateChange = (event) => {
    dispatch(setFilterToDate(event.target.value));
  };

  // Handle restore backup
  const handleRestore = (id) => {
    dispatch(restoreBackupRequest(id));
  };

  // Render action for table row - show restore button for all records
  const renderActions = (row) => {
    return (
      <Tooltip title={t('systemBackup.history.actions.restore')}>
        <IconButton
          color="primary"
          onClick={() => handleRestore(row.id)}
          disabled={restoreLoading}
          id={`systemBackup-backupLogs-table-button-restore-${row.id}`}
          name={`systemBackup-backupLogs-table-button-restore-${row.id}`}
          data-testid={`systemBackup-backupLogs-table-button-restore-${row.id}`}
        >
          <RestoreIcon />
        </IconButton>
      </Tooltip>
    );
  };

  // Helper function to get type label (0=Automatic, 1=Manual)
  const getTypeLabel = (type) => {
    switch (type) {
      case 0:
        return t('systemBackup.history.type.automatic');
      case 1:
        return t('systemBackup.history.type.manual');
      default:
        return type;
    }
  };

  // Helper function to get status chip (0=Processing, 1=Completed, 2=Error)
  const getStatusChip = (status, message) => {
    const statusConfig = {
      0: { label: t('systemBackup.history.status.processing'), color: 'info' },
      1: { label: t('systemBackup.history.status.completed'), color: 'success' },
      2: { label: t('systemBackup.history.status.error'), color: 'error' },
    };
    const config = statusConfig[status] || { label: status, color: 'default' };
    const chip = <Chip label={config.label} color={config.color} size="small" />;

    // Show tooltip with message if message exists
    if (message) {
      return (
        <Tooltip title={message} arrow>
          <span>{chip}</span>
        </Tooltip>
      );
    }
    return chip;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '-';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Format date time
  const formatDateTime = (dateString) => {
    return formatDateTimeWithCulture(dateString, i18n.language) || '-';
  };

  // Backup logs table columns
  const backupLogsColumns = [
    {
      key: 'createdOn',
      label: t('systemBackup.history.table.createdOn'),
      render: (value) => formatDateTime(value),
    },
    {
      key: 'type',
      label: t('systemBackup.history.table.type'),
      render: (value) => getTypeLabel(value),
    },
    {
      key: 'status',
      label: t('systemBackup.history.table.status'),
      render: (value, row) => getStatusChip(value, row?.message),
    },
    {
      key: 'fileSize',
      label: t('systemBackup.history.table.fileSize'),
      render: (value) => formatFileSize(value),
    },
    {
      key: 'totalTime',
      label: t('systemBackup.history.table.totalTime'),
      render: (value) => value ? `${(value / 1000).toFixed(2)} s` : '-',
    },
  ];

  // Determine which fields to show based on schedule type
  const showExecutionTime = scheduleType !== null;
  const showDaysOfWeek = scheduleType === SCHEDULE_TYPE.WEEKLY;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Section */}
      <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StorageIcon fontSize="small" />
            <Typography variant="body2">{t('backup.breadcrumb.backup')}</Typography>
          </Box>
          <Typography variant="body2" color="text.primary">
            {t('backup.systemBackup.breadcrumb')}
          </Typography>
        </Breadcrumbs>

        {/* Page Title */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
            {t('backup.systemBackup.title')}
          </Typography>
        </Box>
      </Box>

      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="system backup tabs"
          >
            <Tab
              icon={<SettingsIcon />}
              iconPosition="start"
              label={t('systemBackup.tabs.backupConfig')}
              id="systemBackup-tab-0"
              aria-controls="systemBackup-tabpanel-0"
              data-testid="systemBackup-tab-backupConfig"
            />
            <Tab
              icon={<HistoryIcon />}
              iconPosition="start"
              label={t('systemBackup.tabs.syncHistory')}
              id="systemBackup-tab-1"
              aria-controls="systemBackup-tabpanel-1"
              data-testid="systemBackup-tab-syncHistory"
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3 }}>
          {/* Tab 1: Backup Config */}
          <TabPanel value={tabValue} index={0}>
            {/* Manual Backup Button */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={manualBackupLoading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                onClick={handleManualBackup}
                disabled={manualBackupLoading}
                id="manualBackup"
                name="manualBackup"
                data-testid="systemBackup-button-manualBackup"
              >
                {t('systemBackup.config.manualBackup')}
              </Button>
            </Box>

            <Paper
              elevation={1}
              sx={{
                p: 3,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'left' }}>
                {t('systemBackup.config.title')}
              </Typography>

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3, justifyContent: 'flex-start' }}>
                {/* Schedule Type */}
                <Box sx={{ minWidth: 300 }}>
                  <Select
                    label={t('systemBackup.config.scheduleType')}
                    value={scheduleType}
                    onChange={handleScheduleTypeChange}
                    options={scheduleTypeOptions}
                    required
                    id="scheduleType"
                    name="scheduleType"
                    data-testid="systemBackup-select-scheduleType"
                  />
                </Box>

                {/* Execution Time - Hidden for Event type */}
                {showExecutionTime && (
                  <Box sx={{ minWidth: 200 }}>
                    <TimePicker
                      label={t('systemBackup.config.executionTime')}
                      value={executionTime}
                      onChange={handleExecutionTimeChange}
                      required
                      id="executionTime"
                      name="executionTime"
                      data-testid="systemBackup-input-executionTime"
                    />
                  </Box>
                )}
              </Box>

              {/* Days of Week - Only shown for Weekly type */}
              {showDaysOfWeek && (
                <Box sx={{ mb: 3, textAlign: 'left' }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1.5, color: 'text.secondary' }}
                  >
                    {t('systemBackup.config.daysOfWeek')} <span style={{ color: '#d32f2f' }}>*</span>
                  </Typography>
                  <ToggleButtonGroup
                    value={selectedDay}
                    exclusive
                    onChange={handleDayChange}
                    aria-label="day of week"
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      justifyContent: 'flex-start',
                    }}
                  >
                    {DAYS_OF_WEEK.map((day, index) => (
                      <ToggleButton
                        key={day.key}
                        value={day.key}
                        id={`day-${index}`}
                        name={`day-${index}`}
                        data-testid={`systemBackup-toggle-day-${index}`}
                        sx={{
                          px: 2,
                          py: 1,
                          border: '1px solid #e0e0e0',
                          '&.Mui-selected': {
                            backgroundColor: '#2d8659',
                            color: '#ffffff',
                            borderColor: '#2d8659',
                            '&:hover': {
                              backgroundColor: '#238b4e',
                            },
                          },
                        }}
                      >
                        {t(day.label)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              )}

              {/* Save Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={saveLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saveLoading}
                  id="saveConfig"
                  name="saveConfig"
                  data-testid="systemBackup-button-saveConfig"
                >
                  {t('systemBackup.config.save')}
                </Button>
              </Box>
            </Paper>
          </TabPanel>

          {/* Tab 2: Sync History */}
          <TabPanel value={tabValue} index={1}>
            {/* Filter Section */}
            <Box sx={{ pb: 2, mb: 2, borderBottom: '1px solid #e5e7eb' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <DateRangePicker
                    label={t('systemBackup.history.filter.dateRange')}
                    fromValue={filterFromDate}
                    toValue={filterToDate}
                    onFromChange={handleFilterFromDateChange}
                    onToChange={handleFilterToDateChange}
                    id="filterDateRange"
                    name="filterDateRange"
                    data-testid="systemBackup-daterangepicker-filter"
                  />
                </Grid>
              </Grid>
            </Box>
            <Table
              id="systemBackup-backupLogs-table"
              columns={backupLogsColumns}
              data={backupLogs}
              loading={backupLogsLoading}
              pagination={true}
              page={backupLogsPage}
              pageSize={backupLogsSize}
              totalItems={backupLogsTotal}
              onPageChange={handleLogsPageChange}
              onPageSizeChange={handleLogsPageSizeChange}
              emptyMessage={t('systemBackup.history.noData')}
              renderActions={renderActions}
            />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
}

export default SystemBackup;
